import { motion } from "framer-motion";
import { Target, Eye, Award } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1553413077-190dd305871c?w=1200&auto=format&fit=crop&q=80";

const TIMELINE = [
  {
    year: "2013",
    title: "Top of the Line Management Solution",
    body: "Ms. Dijamco established the founding firm — built on the philosophy that successful trading requires impeccable management at every level of the supply chain. Meticulous vendor selection, strict quality controls, and robust administrative frameworks earned the firm a reputation for reliability.",
  },
  {
    year: "2018",
    title: "A New Generation Enters the Trade",
    body: "Billy Joe Dijamco entered the entrepreneurial landscape, leveraging his mother's industry network and foundational knowledge to launch a complementary enterprise focused on modernizing logistics and expanding into rapidly-growing consumer-goods categories.",
  },
  {
    year: "Today",
    title: "The Merger — Birth of High Roller",
    body: "Recognizing the unmatched synergy between seamless management infrastructure and high-volume product distribution, mother and son united their ventures. The strategic merger of an elite management firm and a consumer-focused enterprise birthed High Roller Consumer Goods Trading.",
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-slate-50/60">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-3">About Us</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900 leading-tight">
            From the boardroom to the consumer's hands.
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed text-base">
            High Roller Consumer Goods Trading is a testament to becoming the preferred choice of customers and the most reliable regional partner for suppliers — by continuously expanding the importation of high-quality, diverse products that seamlessly contribute to the growth of our country.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed text-base">
            Today, we provide carefully-selected quality brands and merchandise with transaction models tailored to each customer's requirements — keeping satisfaction at the center of every decision.
          </p>

          <div className="mt-10 space-y-6">
            {[
              { Icon: Target, title: "Efficiency", body: "Optimized, cost-effective, and flawlessly-executed logistical steps at every supply-chain level." },
              { Icon: Eye, title: "Quality", body: "Partnering only with trusted manufacturers and brands that meet the highest standards of the communities we serve." },
              { Icon: Award, title: "Integrity", body: "Decades of disciplined management built on transparent administration, rigorous controls, and accountable leadership." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="flex gap-5">
                <span className="shrink-0 w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm text-blue-600 flex items-center justify-center">
                  <Icon size={20} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">{title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Origin Story Timeline */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-12"
        >
          <p className="text-sm uppercase tracking-widest font-semibold text-blue-600 mb-3">Our Story</p>
          <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Two ventures. One legacy. <span className="text-emerald-600">Unmatched synergy.</span>
          </h3>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5">
          {TIMELINE.map((t, i) => (
            <motion.div
              key={t.year}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="relative bg-white border border-slate-200 rounded-2xl p-7"
            >
              <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold tracking-wider">
                {t.year}
              </div>
              <h4 className="font-heading text-lg font-semibold text-slate-900 mt-3 mb-3">{t.title}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
